import React, { useEffect, useState } from "react";
import WhiteLabelService from "../../services/whitelabel";
import {
  Campaign,
  CampaignResponse,
  DroppedCampaign,
  Customization,
  Interaction,
  ReportData,
} from "../../common/types";
import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";
import "./Report.css";
import ReportChart from "../../components/ReportChart";
import AccountChip from "../../components/AccountChip";
import PartialReportNotice from "../../components/PartialReportNotice";
import {
  Chip,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import {
  flowLabels,
  metricKeys,
  metricStyle,
  campaignStateStyle,
} from "../../common/labels";
import {
  formatFullDate,
  formatMediumDate,
  formatShortDate,
} from "../../common/helpers/utils";

export default function Report() {
  const [reportData, setReportData] = useState<ReportData>(null);
  const [customization, setCustomization] = useState<Customization>(null);
  const getReportData = React.useCallback(async () => {
    const service = WhiteLabelService();
    const report = await service.getReportData();
    const campMapped = report.response.campaigns.map(
      (x: CampaignResponse) => x.campaign
    );
    setReportData({
      parameters: report.response.parameters,
      interactions: report.response.interactions,
      campaigns: campMapped,
      droppedCampaigns: Array.isArray(report.response.dropped_campaigns)
        ? (report.response.dropped_campaigns as DroppedCampaign[])
        : [],
    });
  }, [setReportData]);

  useEffect(() => {
    getReportData().catch((error) => {
      Sentry.setExtra("error", error);
      Sentry.captureMessage("getCustomization - Error");
      toast.error("There was an error getting report data, please try again");
    });
  }, [getReportData]);
  //Get whitelabel customization
  useEffect(() => {
    async function getCustomization() {
      const service = WhiteLabelService();
      const customization = await service.getCustomerCustomizations();
      setCustomization(customization);
      document.title = customization.name;
      document
        .getElementById("favicon")
        ?.setAttribute("href", customization.favicon);
    }

    getCustomization().catch((error) => {
      Sentry.setExtra("error", error);
      Sentry.captureMessage("getCustomization - Error");
      toast.error("There was an error getting customization, please try again");
    });
  }, [setCustomization]);
  function invertColor(hex: string) {
    if (!hex) {
      return "#000000";
    }
    if (hex.indexOf("#") === 0) {
      hex = hex.slice(1);
    }
    // convert 3-digit hex to 6-digits.
    if (hex.length === 3) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    if (hex.length !== 6) {
      throw new Error("Invalid HEX color.");
    }
    const r = parseInt(hex.slice(0, 2), 16),
      g = parseInt(hex.slice(2, 4), 16),
      b = parseInt(hex.slice(4, 6), 16);

    // https://stackoverflow.com/a/3943023/112731
    return r * 0.299 + g * 0.587 + b * 0.114 > 186 ? "#000000" : "#FFFFFF";
  }

  const getTotalMetric = (key: string) => {
    return reportData.interactions.reduce((agg: number, i: Interaction) => {
      return agg + i[key as keyof Interaction];
    }, 0);
  };
  const calcSuccess = (campaign: Campaign) => {
    return campaign.metrics.sent_connections === 0
      ? "0%"
      : `${Math.round(
          (campaign.metrics.accepted_connections /
            campaign.metrics.sent_connections) *
            100
        )}%`;
  };
  const calcProgress = (campaign: Campaign) => {
    return `${Math.round(campaign.completion * 100)}%`;
  };
  const calcMetricPercent = (type: string, metric: Interaction) => {
    switch (type) {
      case "accepted_connections":
        if (metric.sent_connections === 0) return 0;
        return Math.round(
          (metric.accepted_connections / metric.sent_connections) * 100
        );
      case "replies":
        if (metric.sent_messages === 0) return 0;
        return Math.round((metric.replies / metric.sent_messages) * 100);
    }
  };
  return (
    <div id="wlr" className="page">
      {reportData ? (
        <>
          <div id="wlr-header">
            {customization?.name} - Interaction Report
            {customization?.logo && (
              <img
                src={customization.logo}
                alt="logo"
                className="logo"
              />
            )}
          </div>
          <div id="wlr-sub-header">
            <div className="wlr-accounts">
              Accounts:{" "}
              {reportData.parameters.account_ids.map((acc) => (
                <AccountChip key={acc} accountId={acc} />
              ))}
            </div>
            <div className="wlr-timeframe">
              <Chip
                size="small"
                sx={{
                  backgroundColor: customization?.primary_color ?? "#000000",
                  color: invertColor(customization?.primary_color ?? "#000000"),
                  fontSize: "10px",
                  lineHeight: "14px",
                  height: "18px",
                }}
                label={`${formatMediumDate(reportData.parameters.from)} 
                - ${formatMediumDate(reportData.parameters.to)}`}
              />
            </div>
          </div>

          <Paper className="wlr-paper">
            <h2>Total Interactions</h2>
            <div className="wlr-chart-wrapper">
              <div className="wlr-chart">
                <ReportChart reportData={reportData} />
              </div>
              <div className="wlr-table legend">
                <Table>
                  <TableHead className="wlr-table-header">
                    <TableRow>
                      <TableCell>Interactions</TableCell>
                      <TableCell>Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {metricKeys.map((key) => (
                      <TableRow
                        key={key}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <TableCell className="wlr-table-label">
                          <div
                            className="wlr-icon-wrapper"
                            style={{
                              backgroundColor:
                                metricStyle[key].color_background,
                            }}
                          >
                            <i
                              className={`ph ph-${metricStyle[key].icon}`}
                              style={{
                                color: metricStyle[key].color,
                                fontSize: "16px",
                                width: "32px",
                                height: "32px",
                                lineHeight: "32px",
                                position: "absolute",
                                textIndent: "8px",
                              }}
                            ></i>
                          </div>
                          {metricStyle[key].label}
                        </TableCell>
                        <TableCell>{getTotalMetric(key)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </Paper>

          <h2>
            Campaigns
            <Chip
              size="small"
              sx={{
                backgroundColor: customization?.primary_color ?? "#000000",
                color: invertColor(customization?.primary_color ?? "#000000"),
                marginLeft: "8px",
                height: "18px",
                fontSize: "10px",
                lineHeight: "14px",
              }}
              label={reportData.campaigns.length}
            />
          </h2>

          <PartialReportNotice dropped={reportData.droppedCampaigns} />

          {reportData.campaigns.map((c) => {
            return (
              <Paper className="wlr-paper campaigns" key={c.id}>
                <div className="title-wrapper">
                  <h2>{c.name}</h2>
                </div>
                <div className="separator"></div>
                <div className="wlr-table campaign-data">
                  <Table>
                    <TableHead className="wlr-table-header">
                      <TableRow>
                        <TableCell className="campaign-list">List</TableCell>
                        <TableCell>Created at</TableCell>
                        <TableCell>State</TableCell>
                        <TableCell>Targets</TableCell>
                        <TableCell>Success</TableCell>
                        <TableCell>Progress</TableCell>
                        <TableCell>Interactions</TableCell>
                        <TableCell>Total leads</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell className="campaign-list">
                          {c.list_name ?? "—"}
                        </TableCell>
                        <TableCell>
                          <Tooltip title={formatFullDate(c.created_at)}>
                            <span>{formatShortDate(c.created_at)}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            sx={{
                              backgroundColor:
                                campaignStateStyle(c.state).color_background,
                              color: campaignStateStyle(c.state).color,
                            }}
                            label={campaignStateStyle(c.state).label}
                          ></Chip>
                        </TableCell>
                        <TableCell>{c.lead_count ?? "—"}</TableCell>
                        <TableCell>{calcSuccess(c)}</TableCell>
                        <TableCell>{calcProgress(c)}</TableCell>
                        <TableCell>{c.metrics.sent_connections}</TableCell>
                        <TableCell>{c.metrics.accepted_connections}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
                <div className="wlr-table">
                  <Table>
                    <TableHead className="wlr-table-header">
                      <TableRow>
                        <TableCell>Interactions</TableCell>
                        {c.flows.map(
                          (flow) =>
                            flow.type !== "CONNECT_OPTIMIZATION" && (
                              <TableCell width={134} key={flow.id}>
                                {flow.name
                                  ? flow.name
                                  : flowLabels[flow.type].label}
                                <Tooltip title={flowLabels[flow.type].tooltip}>
                                  <i className="ph-fill ph-info wlr-info-tooltip"></i>
                                </Tooltip>
                              </TableCell>
                            )
                        )}
                        <TableCell width={134}>Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {metricKeys.map((key) => (
                        <TableRow key={key}>
                          <TableCell className="wlr-table-label">
                            <div
                              className="wlr-icon-wrapper"
                              style={{
                                backgroundColor:
                                  metricStyle[key].color_background,
                              }}
                            >
                             <i
                              className={`ph ph-${metricStyle[key].icon}`}
                              style={{
                                color: metricStyle[key].color,
                                fontSize: "16px",
                                width: "32px",
                                height: "32px",
                                lineHeight: "32px",
                                position: "absolute",
                                textIndent: "8px",
                              }}
                            ></i>
                            </div>
                            {metricStyle[key].label}
                          </TableCell>
                          {c.flows.map(
                            (flow) =>
                              flow.type !== "CONNECT_OPTIMIZATION" && (
                                <TableCell key={flow.id}>
                                  {flow.metrics[key as keyof Interaction]}
                                  {["accepted_connections", "replies"].includes(
                                    key
                                  ) && (
                                    <span className="percent">
                                      ({calcMetricPercent(key, flow.metrics)}
                                      %)
                                    </span>
                                  )}
                                </TableCell>
                              )
                          )}
                          <TableCell>
                            {c.metrics[key as keyof Interaction]}
                            {["accepted_connections", "replies"].includes(
                              key
                            ) && (
                              <span className="percent">
                                ({calcMetricPercent(key, c.metrics)}
                                %)
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </Paper>
            );
          })}
        </>
      ) : (
        <>
          <div
            style={{
              top: "50%",
              left: "50%",
              position: "absolute",
              transform: "translate(-50%, -50%)",
              textAlign: "center",
            }}
          >
            <LinearProgress color="inherit" />
            <h2>Generating report. First-time loading may take a while.</h2>
          </div>
        </>
      )}
    </div>
  );
}
